/**
 * /dashboard/accounting/period-locks — Period Locks admin (G_ACC9).
 *
 * Lock a period (year-end / monthly close) and surface every existing
 * lock with its locker + timestamp. The Unlock action is REVERSIBLE-IN-
 * THE-DESTRUCTIVE-DIRECTION: it removes a constraint, lets pre-lock
 * postings happen again, and is therefore gated server-side on:
 *
 *   (a) RequireCapability("override_lock")  → 403 capability_missing
 *   (b) RequireTOTPEnrolledAndVerified      → 403 totp_enrollment_required
 *                                              or 401 totp_required
 *
 * The frontend mirrors the gates:
 *   - Hides the "Unlock" button when the active user lacks the
 *     "override_lock" capability (presentation only — the backend is
 *     the boundary).
 *   - Catches the 403/401 TOTP errors and routes to /2fa-setup or
 *     /2fa-verify with ?next= back here (mirrors the offboarding
 *     close-cycle dialog + payroll lock pattern).
 *
 * Statutory anchors:
 *   - Companies Act 2013 §128(1)  — true and fair view; locked periods
 *     stamp the books as a system-of-record.
 *   - Companies (Accounts) Amendment Rules 2021 Rule 3(1) — lock /
 *     unlock both write an audit_events row in the SAME TX as the
 *     period_locks mutation.
 *
 * Migration note (Phase 3e — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/accounting/period-locks/page.tsx
 * with:
 *   - "use client" dropped
 *   - useRouter → useNavigate
 *   - usePathname → useLocation({ select: (l) => l.pathname })
 *   - 2fa redirects use Tanstack typed `to:` + search shape.
 */

import { useState } from "react";
import {
  createFileRoute,
  Link,
  useLocation,
  useNavigate,
 useParams,} from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeft,
  Loader2,
  Lock,
  Plus,
  ShieldAlert,
  Unlock,
} from "lucide-react";
import { toast } from "sonner";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  useCreatePeriodLock,
  useDeletePeriodLock,
  usePeriodLocks,
} from "@/hooks/use-accounting";
import { useAuth } from "@/lib/auth";
import { toApiError } from "@/lib/api";
import {
  ACCOUNTING_OVERRIDE_LOCK_CAPABILITY,
  type PeriodLock,
} from "@/types/accounting";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/accounting/period-locks",
)({
  component: PeriodLocksRoute,
});

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function isTotpEnrollmentRequired(errCode: string): boolean {
  return errCode === "totp_enrollment_required";
}
function isTotpRequired(errCode: string): boolean {
  return errCode === "totp_required";
}

// ---------------------------------------------------------------------------
// LOCK dialog — create a new period_lock.
// ---------------------------------------------------------------------------

function LockPeriodDialog({ disabled }: { disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const [periodEnd, setPeriodEnd] = useState("");
  const [notes, setNotes] = useState("");
  const createMut = useCreatePeriodLock();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!periodEnd) {
      toast.error("Pick the last day of the period to lock.");
      return;
    }
    try {
      await createMut.mutateAsync({
        period_end: periodEnd,
        notes: notes || undefined,
      });
      toast.success(`Period ending ${periodEnd} is now locked.`);
      setPeriodEnd("");
      setNotes("");
      setOpen(false);
    } catch (err) {
      const apiErr = toApiError(err);
      toast.error(apiErr.message);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          disabled={disabled}
          className="gap-1.5"
          data-testid="lock-period-trigger"
        >
          <Plus className="h-4 w-4" />
          Lock period
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Lock period</DialogTitle>
            <DialogDescription>
              Refuse postings on or before this date. The engine still writes
              a Rule 3(1) audit row — every lock is preserved for 8 years.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-4">
            <div className="grid gap-1.5">
              <Label htmlFor="pl-period-end">Period end</Label>
              <Input
                id="pl-period-end"
                type="date"
                required
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                YYYY-MM-DD. e.g. 2026-03-31 for FY 2025-26.
              </p>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="pl-notes">Notes (optional)</Label>
              <Textarea
                id="pl-notes"
                maxLength={256}
                rows={3}
                placeholder="Why are you closing this period?"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              type="button"
              onClick={() => setOpen(false)}
              disabled={createMut.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createMut.isPending}>
              {createMut.isPending && (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              )}
              Lock
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// UNLOCK dialog — destructive; step-up TOTP gated server-side.
// ---------------------------------------------------------------------------

function UnlockPeriodDialog({ lock }: { lock: PeriodLock }) {
  const navigate = useNavigate();
  const pathname = useLocation({ select: (l) => l.pathname });
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const delMut = useDeletePeriodLock();

  async function onConfirm() {
    try {
      await delMut.mutateAsync({
        periodEnd: lock.period_end,
        reason: reason || undefined,
      });
      toast.success(`Period ending ${lock.period_end} is now unlocked.`);
      setOpen(false);
      setReason("");
    } catch (err) {
      const apiErr = toApiError(err);

      // Step-up TOTP — mirror offboarding/close + payroll/lock pattern.
      if (isTotpEnrollmentRequired(apiErr.error)) {
        toast.error(
          "Enrol in two-factor authentication to unlock closed periods.",
        );
        void navigate({
          to: "/2fa-setup",
          search: { required: 1, next: pathname },
        });
        return;
      }
      if (isTotpRequired(apiErr.error)) {
        toast.error(
          "Two-factor verification required to unlock the period.",
        );
        void navigate({
          to: "/2fa-verify",
          search: { next: pathname },
        });
        return;
      }
      if (apiErr.error === "capability_missing") {
        toast.error(
          "You don't have the override_lock capability. Ask a company admin to grant it.",
        );
        setOpen(false);
        return;
      }
      toast.error(apiErr.message);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="destructive"
          size="sm"
          className="gap-1.5"
          data-testid={`unlock-${lock.period_end}`}
        >
          <Unlock className="h-3.5 w-3.5" />
          Unlock
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-destructive" />
            Unlock period ending {lock.period_end}?
          </DialogTitle>
          <DialogDescription>
            Removing the lock re-opens this period to new postings. The Rule
            3(1) audit trail captures who unlocked it and why — visible to
            every auditor in the Audit Trail surface.
          </DialogDescription>
        </DialogHeader>
        <Alert variant="destructive" className="my-2">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>This action is irreversible.</AlertTitle>
          <AlertDescription>
            Re-locking does not roll back any postings made while the period
            was open. You will be prompted for two-factor verification before
            the unlock takes effect.
          </AlertDescription>
        </Alert>
        <div className="grid gap-1.5 py-2">
          <Label htmlFor="pl-reason">Reason (recorded in audit trail)</Label>
          <Textarea
            id="pl-reason"
            maxLength={512}
            rows={3}
            placeholder="Why is this period being re-opened?"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            type="button"
            onClick={() => setOpen(false)}
            disabled={delMut.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            type="button"
            onClick={onConfirm}
            disabled={delMut.isPending}
          >
            {delMut.isPending && (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            )}
            Unlock
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function PeriodLocksRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { user } = useAuth();
  const canOverride =
    user?.capabilities?.includes(ACCOUNTING_OVERRIDE_LOCK_CAPABILITY) ?? false;

  const query = usePeriodLocks();
  const locks = query.data?.items ?? [];
  const loading = query.isPending;
  const error = query.isError ? toApiError(query.error).message : null;

  return (
    <AccountingModuleShell
      title="Period Locks"
      description="Lock a period to refuse new postings on or before that date. Unlocking requires the override_lock capability + 2FA."
      actions={
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" className="gap-1.5">
            <Link to="/app/$tenantSlug/accounting/reports" params={{ tenantSlug }}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
          <LockPeriodDialog />
        </div>
      }
    >
      {!canOverride && (
        <Alert className="mt-4">
          <Lock className="h-4 w-4" />
          <AlertTitle>Read-only on unlock</AlertTitle>
          <AlertDescription>
            You can lock periods, but you do not have the
            <span className="mx-1 rounded bg-muted px-1 py-0.5 font-mono text-xs">
              override_lock
            </span>
            capability needed to unlock. Ask a company admin to grant it
            before raising a reversal.
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive" className="mt-4">
          <AlertTitle>Could not load period locks</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card className="mt-4">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-40">Period end</TableHead>
                <TableHead className="w-56">Locked at</TableHead>
                <TableHead>Locked by</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="w-32 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    <TableCell colSpan={5}>
                      <Skeleton className="h-8" />
                    </TableCell>
                  </TableRow>
                ))
              ) : locks.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="py-12 text-center text-sm text-muted-foreground"
                  >
                    No period locks yet. Lock a period to close the books for
                    that month or year.
                  </TableCell>
                </TableRow>
              ) : (
                locks.map((lock) => (
                  <TableRow
                    key={lock.period_end}
                    data-testid={`lock-${lock.period_end}`}
                  >
                    <TableCell className="font-mono">
                      {lock.period_end}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground tabular-nums">
                      {formatTimestamp(lock.locked_at)}
                    </TableCell>
                    <TableCell>
                      {lock.locked_by_name || `User #${lock.locked_by_id}`}
                      {lock.locked_by_email && (
                        <div className="text-xs text-muted-foreground">
                          {lock.locked_by_email}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {lock.notes ?? (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {canOverride ? <UnlockPeriodDialog lock={lock} /> : null}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </AccountingModuleShell>
  );
}
